import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import authFetch from "../utils/authFetch";
import { OpenAiPromptType } from "../utils/enums";
import { toast } from "react-toastify";

// OpenAI Prompt Types enum
// const OpenAiPromptType = {
//   USER_PROFILE_GENERAL_CONDITIONS:'USER_PROFILE_GENERAL_CONDITIONS',
//   SUMMARISATION_INSTRUCTION:'SUMMARISATION_INSTRUCTION',
//   CHATBOT_MODE : 'CHATBOT_MODE',
//   YEARLY_MONTHLY_RULES:'YEARLY_MONTHLY_RULES',
//   KEY_POINT:'KEY_POINT',
//   CAUTION_REMEDY_PREDICTION : 'CAUTION_REMEDY_PREDICTION',
//   // HOMEPAGE_DAILY_PREDICTION:'HOMEPAGE_DAILY_PREDICTION',
//   // HOMEPAGE_MONTHLY_PREDICTION:'HOMEPAGE_MONTHLY_PREDICTION',
//   // HOMEPAGE_YEARLY_PREDICTION:'HOMEPAGE_YEARLY_PREDICTION',
// };

const AiSettingsTab = () => {
  const [prompts, setPrompts] = useState({
    [OpenAiPromptType.PROFILE]: "",
    [OpenAiPromptType.GENERAL_CONDITIONS]: "",
    [OpenAiPromptType.YEARLY_PREDICTION]: "",
    [OpenAiPromptType.MONTHLY_PREDICTION]: "",
    [OpenAiPromptType.DAY_PREDICTION]: "",
    [OpenAiPromptType.PROMISE]: "",
    [OpenAiPromptType.KEY]: "",
    [OpenAiPromptType.CAUTION_PREDICTION]: "",
    [OpenAiPromptType.REMEDY_PREDICTION]: "",
    [OpenAiPromptType.CHATBOT_MODE]: "",
    [OpenAiPromptType.PREDICTION_METER]: "",
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load existing prompts on component mount
  useEffect(() => {
    const loadExistingPrompts = async () => {
      try {
        setInitialLoading(true);
        const response = await authFetch.get(
          "/prediction/admin/open-ai-settings/prompt"
        );
        if (
          response.data &&
          response.data.success &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          const loadedPrompts = {};
          response.data.data.forEach((item) => {
            if (item.type && item.prompt) {
              loadedPrompts[item.type] = item.prompt;
            }
          });
          setPrompts((prev) => ({ ...prev, ...loadedPrompts }));
        }
      } catch (err) {
        console.error("Error loading existing prompts:", err);
        // Don't show error for initial load, just use empty prompts
      } finally {
        setInitialLoading(false);
      }
    };

    loadExistingPrompts();
  }, []);

  const handlePromptChange = (type, value) => {
    setPrompts((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Filter out empty prompts and create payload array
      const promptsToSave = Object.entries(prompts)
        .filter(([type, prompt]) => prompt.trim() !== "")
        .map(([type, prompt]) => ({
          prompt: prompt.trim(),
          type: type,
        }));

      if (promptsToSave.length === 0) {
        toast.error("Please enter at least one prompt before saving.");
        return;
      }

      console.log("promptsToSave", promptsToSave);
      // Send PUT request with array of prompts
      await authFetch.put(
        "/prediction/admin/open-ai-settings/prompt",
        promptsToSave
      );
      toast.success("AI Settings saved successfully!");
    } catch (err) {
      console.error("Error saving prompts:", err);
      toast.error(
        err.response?.data?.message ||
          "Failed to save AI settings. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const renderPromptSection = (title, type) => {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          value={prompts[type] || ""}
          onChange={(e) => handlePromptChange(type, e.target.value)}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#d1d5db",
              },
              "&:hover fieldset": {
                borderColor: "#9ca3af",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#3b82f6",
              },
            },
          }}
        />
      </Box>
    );
  };

  // Show loader during initial data fetch
  if (initialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          p: 3,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        {(() => {
          const sections = [
            {
              title: "Profile",
              type: OpenAiPromptType.PROFILE,
            },
            {
              title: "General Conditions",
              type: OpenAiPromptType.GENERAL_CONDITIONS,
            },
            {
              title: "Yearly Rules",
              type: OpenAiPromptType.YEARLY_PREDICTION,
            },
            {
              title: "Monthly Rules",
              type: OpenAiPromptType.MONTHLY_PREDICTION,
            },
            {
              title: "Day Rules",
              type: OpenAiPromptType.DAY_PREDICTION,
            },
            {
              title: "Promise",
              type: OpenAiPromptType.PROMISE,
            },
            {
              title: "Key",
              type: OpenAiPromptType.KEY,
            },
            {
              title: "Caution",
              type: OpenAiPromptType.CAUTION_PREDICTION,
            },
            {
              title: "Remedy",
              type: OpenAiPromptType.REMEDY_PREDICTION,
            },
            {
              title: "ChatBot Mode",
              type: OpenAiPromptType.CHATBOT_MODE,
            },
            {
              title: "Predictions Meter",
              type: OpenAiPromptType.PREDICTION_METER,
            },
            // {
            //   title: 'Homepage-Daily Prediction',
            //   type: OpenAiPromptType.HOMEPAGE_DAILY_PREDICTION,
            // },
            // {
            //   title: 'Homepage-Monthly Prediction',
            //   type: OpenAiPromptType.HOMEPAGE_MONTHLY_PREDICTION,
            // },
            // {
            //   title: 'Homepage-Yearly Prediction',
            //   type: OpenAiPromptType.HOMEPAGE_YEARLY_PREDICTION,
            // },
          ];

          return sections.map((section, index) => (
            <Fragment key={section.type}>
              {renderPromptSection(section.title, section.type)}
              {index < sections.length - 1 && <Divider sx={{ my: 2 }} />}
            </Fragment>
          ));
        })()}
      </Box>

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={loading}
        sx={{
          bgcolor: "#3b82f6",
          "&:hover": {
            bgcolor: "#2563eb",
          },
        }}
      >
        {loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          "Save AI Settings"
        )}
      </Button>
    </Box>
  );
};

export default AiSettingsTab;
